import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormHelperText, Typography, makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import InputField from '../../../shared/inputField';
import AddFileField from '../../../shared/addFileField';
import CustomCheckbox from '../../../shared/customCheckbox';
import NextButton from '../../../shared/nextButton';
import BackLink from '../../../shared/backLink';
import TextBoxWithTooltip from '../../../shared/textBoxWithTooltip';
import { FIELD_TYPES } from '../../../../constants/fieldTypes';
import {
  KADAR_LANDING,
  PRIVACY_POLICY_PAGE_URL,
  TERMS_OF_SERVICE_PAGE_URL,
} from '../../../../constants/appConfig';
import { APPLY_JOB_FIELD_NAMES, getApplyJobFields } from '../config';
import { ReactComponent as LogoIcon } from '../../../../assets/icons/kadar_logo_mark_app.svg';
import { isEmpty, validateFileType } from '../../../../utility/validation';

const { TEXT, UPLOAD_FILE, CHECKBOX } = FIELD_TYPES;

const CONTENT_MAX_WIDTH = 1200;
const FORM_MAX_WIDTH = 1100;
const MIDDLE_FORM_MAX_WIDTH = 900;
const SMALL_FORM_MAX_WIDTH = 590;

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    main: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100vh',
      '@supports (height: 100dvh)': {
        height: '100dvh',
      },
      overflow: 'hidden',
    },
    pageBody: {
      flex: '1 1 auto',
      display: 'flex',
      justifyContent: 'center',
      minWidth: 0,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      padding: spacing(3, 2),
      paddingBottom: spacing(2),
      [breakpoints.up('sm')]: {
        padding: spacing(6, 4),
      },
      [breakpoints.between('sm', 'md')]: {
        padding: spacing(6, 3),
      },
      [breakpoints.up('md')]: {
        padding: spacing(10, 11),
      },
    },
    wrapper: {
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      minWidth: 0,
      height: 'fit-content',
      display: 'grid',
      gap: 32,
      justifyContent: 'center',
      [breakpoints.between('md', 'lg')]: {
        maxWidth: MIDDLE_FORM_MAX_WIDTH,
      },
      [breakpoints.up('lg')]: {
        maxWidth: CONTENT_MAX_WIDTH,
      },
    },
    header: {
      display: 'grid',
      justifyContent: 'center',
      gap: spacing(2),
      width: '100%',
      minWidth: 0,
      boxSizing: 'border-box',
    },
    titleWrapper: {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      paddingBottom: spacing(4),
      borderBottom: `1px solid ${primary.bluish7}`,
      maxWidth: '100%',
      width: '100%',
      boxSizing: 'border-box',
      minWidth: 0,
      margin: '0 auto',
      [breakpoints.up('md')]: {
        maxWidth: CONTENT_MAX_WIDTH,
        width: CONTENT_MAX_WIDTH,
      },
    },
    title: {
      fontSize: 28,
      maxWidth: '100%',
      width: '100%',
      textAlign: 'center',
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'unset',
      wordBreak: 'break-word',
      boxSizing: 'border-box',
      [breakpoints.up('sm')]: {
        fontSize: 40,
      },
      [breakpoints.between('sm', 'md')]: {
        fontSize: 36,
      },
      [breakpoints.up('md')]: {
        fontSize: 50,
      },
    },
    formCard: {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      margin: '0 auto',
      display: 'grid',
      boxSizing: 'border-box',
      [breakpoints.up('lg')]: {
        maxWidth: FORM_MAX_WIDTH,
        width: FORM_MAX_WIDTH,
      },
      [breakpoints.between('md', 'lg')]: {
        width: MIDDLE_FORM_MAX_WIDTH,
      },
      [breakpoints.between('sm', 'md')]: {
        width: SMALL_FORM_MAX_WIDTH,
      },
    },
    hasError: {
      marginBottom: spacing(1),
    },
    field: {
      marginBottom: spacing(6),
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      minWidth: 0,
      [breakpoints.up('lg')]: {
        maxWidth: FORM_MAX_WIDTH,
        width: FORM_MAX_WIDTH,
      },
      [breakpoints.between('md', 'lg')]: {
        width: MIDDLE_FORM_MAX_WIDTH,
      },
    },
    checkboxContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      marginTop: spacing(2),
    },
    checkboxDescription: {
      marginTop: 3,
      marginLeft: spacing(2),
      fontSize: 14,
      lineHeight: '20px',
      color: primary.bluish2,
    },
    checkboxLink: {
      color: primary.main,
    },
    backLink: {
      marginBottom: spacing(2),
      textAlign: 'center',
      '& > p': {
        fontFamily: 'ProximaNova-Regular',
      },
      '&:hover p': {
        fontFamily: 'ProximaNova-Regular',
        color: primary.bluish1,
        textShadow: `.01em 0 0 ${primary.bluish1}`,
      },
    },
    submitButton: {
      marginTop: spacing(2),
      width: 300,
      maxWidth: 300,
      boxSizing: 'border-box',
      [breakpoints.up('md')]: {
        width: 360,
        maxWidth: 360,
      },
      '& button': {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        backgroundColor: primary.green5,
        borderColor: primary.green5,
        color: primary.bluish1,
        '& g, & path': {
          fill: primary.bluish1,
        },
        '&:hover': {
          boxShadow: `0 0 0 1px ${primary.green5}`,
          backgroundColor: primary.green5,
        },
        '&:disabled': {
          color: primary.bluish1,
          '& g, & path': {
            fill: primary.bluish1,
          },
          opacity: '70%',
          backgroundColor: primary.green5,
        },
      },
    },
    nexButtonWrapper: {
      width: '100%',
      display: 'grid',
      justifyContent: 'center',
    },
    checked: {
      marginLeft: '-79px',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      flexShrink: 0,
      gap: 8,
      textDecoration: 'none',
      width: '100%',
      boxSizing: 'border-box',
      backgroundColor: primary.white,
      '&:hover': {
        textDecoration: 'underline',
      },
      padding: spacing(3, 2),
      [breakpoints.down('sm')]: {
        paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      },
      [breakpoints.up('sm')]: {
        padding: spacing(6, 4),
      },
      [breakpoints.up('md')]: {
        padding: spacing(6, 11),
      },
    },
    logoIcon: {
      width: 24,
      height: 24,
    },
    rateLimit: {
      justifyContent: 'center',
    },
    upload: {
      width: 'fit-content',
      maxWidth: '100%',
      boxSizing: 'border-box',
      minWidth: 0,
      [breakpoints.up('sm')]: {
        maxWidth: FORM_MAX_WIDTH,
      },
    },
    customUrlLink: {
      maxWidth: 280,
      marginRight: 0,
      [breakpoints.up('sm')]: {
        maxWidth: 290,
        marginRight: spacing(2),
      },
      [breakpoints.up('md')]: {
        maxWidth: 364,
        marginRight: spacing(2),
      },
    },
  })
);

const fields = getApplyJobFields();

const ApplyJobFormLayout = ({
  translations,
  jobTitle = '',
  token,
  navigate,
  values,
  errors,
  isSubmitting,
  rateLimitErrorMessage = '',
  setFieldValue,
  setFieldError,
  handleChange,
  handleBlur,
  handleSubmit,
}) => {
  const classes = useStyles();
  const { validationMessages } = translations;
  const hasRateLimitError = isEmpty(rateLimitErrorMessage);
  const getErrorMessage = fieldName => {
    const errorType = errors[fieldName];
    if (!errorType) {
      return '';
    }
    return validationMessages[errorType] || '';
  };

  const handleFileUpload = file => {
    if (file && !validateFileType(file, 'application/pdf')) {
      setFieldError(APPLY_JOB_FIELD_NAMES.CV, 'fileType');
      return;
    }

    setFieldValue(APPLY_JOB_FIELD_NAMES.CV, file);
  };

  const handleFileReject = () => {
    setFieldError(APPLY_JOB_FIELD_NAMES.CV, 'fileType');
  };

  const handleTermsChange = () => {
    setFieldValue(APPLY_JOB_FIELD_NAMES.TERMS_ACCEPTED, !values.terms_accepted);
  };

  const fieldClasses = hasError =>
    classNames({
      [classes.field]: !hasError,
      [classes.hasError]: hasError,
    });

  const renderField = field => {
    const errorMessage = getErrorMessage(field.name);
    const hasError = !!errors[field.name];

    if (field.type === UPLOAD_FILE) {
      const labels = translations.formLabels[field.translationKey];
      return (
        <div
          key={field.name}
          className={classNames(fieldClasses(hasError), classes.upload)}
        >
          <AddFileField
            label={labels.label}
            customUrlLinkClass={classes.customUrlLink}
            placeholder={labels.placeholder}
            uploadedFiles={values[field.name] || null}
            isMultiple={field.isMultiple}
            types={field.types}
            hasError={hasError}
            errorMessage={errorMessage}
            onAddFile={handleFileUpload}
            onRejectFile={handleFileReject}
          />
        </div>
      );
    }

    if (field.type === CHECKBOX) {
      return (
        <div
          key={field.name}
          className={classNames(
            fieldClasses(hasError),
            classes.checkboxContainer
          )}
        >
          <CustomCheckbox
            isChecked={!!values.terms_accepted}
            hasError={hasError}
            errorMessage={errorMessage}
            onChange={handleTermsChange}
          />
          <Typography
            className={classNames(classes.checkboxDescription, {
              [classes.checked]: errors[field.name],
            })}
          >
            {translations.agree}{' '}
            <Link
              className={classes.checkboxLink}
              to={TERMS_OF_SERVICE_PAGE_URL}
              target="_blank"
            >
              {translations.terms_of_service.name}
            </Link>{' '}
            {translations.and}{' '}
            <Link
              className={classes.checkboxLink}
              to={PRIVACY_POLICY_PAGE_URL}
              target="_blank"
            >
              {translations.privacy_policy.name}
            </Link>
          </Typography>
        </div>
      );
    }

    const labels = translations.formLabels[field.translationKey];
    return (
      <div key={field.name} className={fieldClasses(hasError)}>
        <InputField
          placeholder={labels.placeholder}
          label={labels.label}
          type={TEXT}
          name={field.name}
          error={hasError}
          errorMessage={errorMessage}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values[field.name] || ''}
          fullWidth
        />
      </div>
    );
  };

  return (
    <div className={classes.main}>
      <div className={classes.pageBody}>
        <div className={classes.wrapper}>
          <div className={classes.header}>
            {jobTitle ? (
              <>
                <div className={classes.titleWrapper}>
                  <TextBoxWithTooltip
                    className={classes.title}
                    text={jobTitle}
                    variant="h1"
                  />
                </div>
                <BackLink
                  className={classes.backLink}
                  title={translations.backToJob}
                  to={`/job/${token}`}
                  navigate={navigate}
                />
              </>
            ) : null}
          </div>
          <form className={classes.formCard} onSubmit={handleSubmit}>
            {fields.map(renderField)}
            <div className={classes.nexButtonWrapper}>
              {hasRateLimitError && (
                <FormHelperText
                  className={classes.rateLimit}
                  error={hasRateLimitError}
                >
                  {rateLimitErrorMessage}
                </FormHelperText>
              )}
              <NextButton
                customClass={classes.submitButton}
                isDisabled={isSubmitting || !values.terms_accepted}
                buttonLabel={translations.submit}
              />
            </div>
          </form>
        </div>
      </div>
      <a
        className={classes.footer}
        href={KADAR_LANDING}
        target="_blank"
        rel="noopener noreferrer"
      >
        <LogoIcon className={classes.logoIcon} />
        <Typography variant="body2">{translations.poweredBy}</Typography>
      </a>
    </div>
  );
};

ApplyJobFormLayout.propTypes = {
  translations: PropTypes.object.isRequired,
  jobTitle: PropTypes.string,
  token: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldError: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  rateLimitErrorMessage: PropTypes.string,
};

export default ApplyJobFormLayout;
